<script setup lang="ts">
import { computed, onBeforeMount, onMounted, onUnmounted, nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import ContentView from './ContentView.vue'
import SearchBar from '@/components/widgets/stores-list/SearchBar.vue'
import ErrorView from './ErrorView.vue'
import SpinnerOverlay from '@/components/common/SpinnerOverlay.vue'
import HeaderItem from '@/components/widgets/stores-list/HeaderItem.vue'
import SalesRepContactButton from '@/components/common/SalesRepContactButton.vue'
import AxeLogo from '@/components/icons/AxeLogo.vue'
import PharmaProgramsLogo from '@/components/icons/PharmaProgramsLogo.vue'
import { formatCurrency } from '@/components/common/common'
import { useThemeStore, useValueDataStore } from '@/stores'
import { EntityType, type StoreSummary } from '@/data'

const BRANDS = ['Amgevita', 'Hadlima', 'Prolia', 'Steqeyma']
const BRAND_KEYS = ['amgevita', 'hadlima', 'prolia', 'steqeyma'] as const

type SortField =
  | 'storeName'
  | 'amgevitaValue'
  | 'hadlimaValue'
  | 'proliaValue'
  | 'steqeymaValue'
  | 'othersValue'
  | 'totalAxeValue'

const themeStore = useThemeStore()
const { themeColors } = themeStore

const { isValueDataStoreLoading, isError, filteredGroupId, searchValue } = storeToRefs(useValueDataStore())
const { loadValueData, getStoresSummaryByGroupId } = useValueDataStore()

const tableElement = ref<HTMLElement | null>(null)
const currentPage = ref(1)
const rowsPerPage = ref(8)
const selectedStoreId = ref<string | null>(null)
const sortField = ref<SortField | null>(null)
const sortDirection = ref<'asc' | 'desc' | null>(null)

const getMinRowHeightPx = () => {
const remHeight = 3.75 // 60px at 16px base
  if (typeof window === 'undefined') return remHeight * 16
  const base = parseFloat(getComputedStyle(document.documentElement).fontSize || '16')
  return remHeight * base
}

const updateRowsPerPage = () => {
  if (!tableElement.value) return
  const tableHeight = tableElement.value.clientHeight
  const minRowHeight = getMinRowHeightPx()
  const newRowsPerPage = Math.floor(tableHeight / minRowHeight)
  rowsPerPage.value = Math.max(newRowsPerPage, 1)
}

const handleResize = () => {
  updateRowsPerPage()
}

onBeforeMount(() => {
  window.addEventListener('resize', handleResize)
})

onMounted(async () => {
  await nextTick()
  updateRowsPerPage()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

watch(
  () => filteredGroupId.value,
  async () => {
    selectedStoreId.value = null
    currentPage.value = 1
    await nextTick()
    updateRowsPerPage()
  }
)

const preparedData = computed(() => {
  const list = getStoresSummaryByGroupId()
  if (!list || !list.length) return []

  return list.map((store: StoreSummary) => {
    const brandSet = new Set(BRANDS)
    const brandData = BRAND_KEYS.map((key, idx) => {
      const name = BRANDS[idx]!
      return {
        key,
        name,
        value: store.axeBrands?.[name]?.value ?? 0,
        opportunity: store.axeBrands?.[name]?.opportunity ?? 0
      }
    })

    const others = Object.entries(store.axeBrands ?? {}).reduce(
      (acc, [name, data]) => {
        if (brandSet.has(name)) return acc
        const value = data?.value ?? 0
        const opportunity = data?.opportunity ?? 0
        acc.value += value
        acc.opportunity += opportunity
        return acc
      },
      { value: 0, opportunity: 0 }
    )

    const totalAxeValue = brandData.reduce((acc, item) => acc + item.value, 0) + others.value;
    const totalAxeOpportunity = brandData.reduce((acc, item) => acc + item.opportunity, 0) + others.opportunity;

    return {
      storeId: store.storeId,
      storeName: store.storeName,
      amgevitaValue: brandData[0]?.value ?? 0,
      hadlimaValue: brandData[1]?.value ?? 0,
      proliaValue: brandData[2]?.value ?? 0,
      steqeymaValue: brandData[3]?.value ?? 0,
      othersValue: others.value,
      brandData,
      others,
      totalAxeValue,
      totalAxeOpportunity
    }
  })
})

interface IAggregated {
  storeName: string;
  brands: { key: string, value: number, opportunity: number }[],
  totalAxeValue: number;
  totalAxeOpportunity: number;
  othersValue: number;
  othersOpportunity: number;
}

const aggregateRow = computed(() => {
  if (!filteredGroupId.value) return null
  const list = preparedData.value
  if (!list.length) return null

  const initial: IAggregated = {
    storeName: 'Group Total',
    brands: BRAND_KEYS.map((key) => ({ key, value: 0, opportunity: 0 })),
    totalAxeValue: 0,
    totalAxeOpportunity: 0,
    othersValue: 0,
    othersOpportunity: 0
  }

  const aggregated: IAggregated = list.reduce((acc, item) => {
    item.brandData.forEach((brand) => {
      const target = acc.brands.find((b) => b.key === brand.key)
      if (target) {
        target.value += brand.value
        target.opportunity += brand.opportunity
      }
    })
    acc.othersValue += item.others.value
    acc.othersOpportunity += item.others.opportunity
    acc.totalAxeValue += item.totalAxeValue;
    acc.totalAxeOpportunity += item.totalAxeOpportunity;
    return acc;
  }, initial)

  return aggregated
})

const sortedData = computed(() => {
  const data = [...preparedData.value]
  if (!sortField.value || !sortDirection.value) {
    return data.sort((a, b) => b.totalAxeValue - a.totalAxeValue)
  }

  const key = sortField.value as SortField

  const getValue = (item: any) => {
    if (key === 'storeName') return item.storeName?.toLowerCase?.() ?? ''
    return (item as Record<string, number>)[key] ?? 0
  }

  return data.sort((a, b) => {
    const valA = getValue(a)
    const valB = getValue(b)

    if (key === 'storeName') {
      if (valA < valB) return sortDirection.value === 'asc' ? -1 : 1
      if (valA > valB) return sortDirection.value === 'asc' ? 1 : -1
      return 0
    }

    const numA = Number(valA) || 0
    const numB = Number(valB) || 0
    return sortDirection.value === 'asc' ? numA - numB : numB - numA
  })
})

const totalPages = computed(() => {
  if (!sortedData.value.length) return 0
  return Math.ceil(sortedData.value.length / rowsPerPage.value)
})

const paginatedData = computed(() => {
  if (!sortedData.value.length) return []
  const start = (currentPage.value - 1) * rowsPerPage.value
  return sortedData.value.slice(start, start + rowsPerPage.value)
})

watch(
  () => sortedData.value,
  () => {
    currentPage.value = 1
    updateRowsPerPage()
  }
)

watch(
  isValueDataStoreLoading,
  async (loading) => {
    if (!loading) {
      await nextTick()
      updateRowsPerPage()
    }
  },
  { immediate: true }
)

watch(
  () => filteredGroupId.value,
  () => {
    selectedStoreId.value = null
    currentPage.value = 1
  }
)

const displayedPages = computed(() => {
  if (totalPages.value) {
    const pages: (number | string)[] = []
    const lastPage = totalPages.value

    if (lastPage <= 4) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage.value < 3) {
        pages.push(1, 2, 3, '...', lastPage)
      } else if (currentPage.value > lastPage - 2) {
        pages.push(1, '...', lastPage - 2, lastPage - 1, lastPage)
      } else {
        pages.push(
          1,
          '...',
          currentPage.value - 1,
          currentPage.value,
          currentPage.value + 1,
          '...',
          lastPage
        )
      }
    }
    return pages
  }
  return []
})

const goToPage = (page: number | string) => {
  if (typeof page === 'string') return
  currentPage.value = page
}

const handleLoadData = (storeId: string) => {
  loadValueData(EntityType.STORE, storeId)
  selectedStoreId.value = storeId
}

const setSort = (field: string) => {
  const typedField = field as SortField
  if (sortField.value !== typedField) {
    sortField.value = typedField
    sortDirection.value = 'asc'
  } else if (sortDirection.value === 'asc') {
    sortDirection.value = 'desc'
  } else if (sortDirection.value === 'desc') {
    sortField.value = null
    sortDirection.value = null
  } else {
    sortDirection.value = 'asc'
  }
}
</script>

<template>
  <SpinnerOverlay v-if="isValueDataStoreLoading" />
  <ErrorView v-else-if="isError" />
  <ContentView v-else>
    <template #top>
      <AxeLogo width="6.5625rem" height="3.125rem" :fill="themeColors.colorText" />
      <PharmaProgramsLogo width="20.3125rem" height="3.125rem" :fill="themeColors.colorText" />
      <SalesRepContactButton label="Contact your Axe Rep" type="arrotex" />
    </template>
    <template #middle>
      <div class="stores-list-container">
        <div class="search-bar-container">
          <SearchBar v-model:search="searchValue" />
        </div>

        <div
          v-if="aggregateRow"
          class="store-list-item aggregate"
        >
          <div>
            <div class="store-name-cell no-tooltip">
              <span class="store-name">{{ aggregateRow.storeName }}</span>
            </div>
          </div>
          <div class="value-pair">
            <span class="value">{{ formatCurrency(aggregateRow.totalAxeValue) }}</span>
            <span class="value">/</span>
            <span class="opportunity">{{ formatCurrency(aggregateRow.totalAxeOpportunity) }}</span>
          </div>
          <div class="value-pair" v-for="brand in aggregateRow.brands" :key="brand.key">
            <span class="value">{{ formatCurrency(brand.value) }}</span>
            <span class="value">/</span>
            <span class="opportunity">{{ formatCurrency(brand.opportunity) }}</span>
          </div>
          <div class="value-pair">
            <span class="value">{{ formatCurrency(aggregateRow.othersValue) }}</span>
            <span class="value">/</span>
            <span class="opportunity">{{ formatCurrency(aggregateRow.othersOpportunity) }}</span>
          </div>
        </div>

        <div class="store-list-item header" :class="{ margin: !aggregateRow }">
          <HeaderItem
            fieldName="Store Name"
            fieldKey="storeName"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
          <HeaderItem
            fieldName="Total"
            fieldKey="totalAxeValue"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
          <HeaderItem
            v-for="brand in BRANDS"
            :key="brand"
            :fieldName="brand"
            :fieldKey="`${brand.toLowerCase()}Value`"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
          <HeaderItem
            fieldName="All Others"
            fieldKey="othersValue"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
        </div>

        <div class="inner-container" ref="tableElement">
          <div
            class="store-list-item"
            v-for="store in paginatedData"
            :key="store.storeId"
            @click="handleLoadData(store.storeId)"
            :class="{ selected: store.storeId === selectedStoreId }"
          >
            <div class="store-name-cell" :data-full-name="store.storeName">
              <span class="store-name">{{ store.storeName }}</span>
            </div>
            <div class="value-pair">
              <span class="value">{{ formatCurrency(store.totalAxeValue) }}</span>
              <span class="value">/</span>
              <span class="opportunity">{{ formatCurrency(store.totalAxeOpportunity) }}</span>
            </div>
            <div v-for="brand in store.brandData" :key="brand.name" class="value-pair">
              <span class="value">{{ formatCurrency(brand.value) }}</span>
              <span class="value">/</span>
              <span class="opportunity">{{ formatCurrency(brand.opportunity) }}</span>
            </div>
            <div class="value-pair">
              <span class="value">{{ formatCurrency(store.others.value) }}</span>
              <span class="value">/</span>
              <span class="opportunity">{{ formatCurrency(store.others.opportunity) }}</span>
            </div>
          </div>
        </div>

        <div class="pagination">
          <button
            v-for="page in displayedPages"
            :key="page"
            :disabled="page === '...'"
            :class="{ active: page === currentPage }"
            @click="goToPage(page)"
          >
            {{ page === '...' ? '...' : `Page ${page}` }}
          </button>
        </div>
      </div>
    </template>
  </ContentView>
</template>

<style scoped>
.stores-list-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  width: 100%;
  height: 85vh;
}

.search-bar-container {
  align-self: flex-start;
  width: 33.33%;
}

.inner-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  display: flex;
  overflow-y: auto;
}

.store-list-item {
  text-align: center;
  display: grid;
  grid-template-columns: 22% repeat(6, 13%);
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 3.125rem;
  padding: 0.75rem;
  border-radius: 25rem;
  transition: background-color 0.5s ease;
  background-color: var(--color-background-bright);
  cursor: pointer;
  margin-bottom: 0.75rem;
}

.store-list-item.aggregate {
  background-color: var(--color-background-bright);
  color: var(--color-text);
  cursor: default;
  margin-top: 2rem;
  border-radius: 5rem;
}

.store-list-item.aggregate .store-name-cell {
  font-weight: 900;
}

.store-list-item > div:first-child {
  text-align: start;
  display: flex;
  align-items: center;
  width: 100%;
}

.store-list-item.aggregate .value {
  color: var(--color-text);
}

.store-name-cell {
  width: 100%;
  position: relative;
}

.store-name-cell.no-tooltip::after {
  display: none;
}

.store-list-item.header {
  font-size: var(--font-size-small);
  padding: 0;
  padding-inline: 0.75rem;
  background-color: unset;
  height: 5%;
  cursor: default;
}

.store-list-item.header .store-name {
  font-size: var(--font-size-small);
}

.store-list-item.header.margin {
  margin-top: 2rem;
}

.store-list-item.selected {
  background-color: var(--color-text);
  color: var(--color-background-bright);
}

.store-list-item.selected .value {
  color: var(--color-background-bright);
}

.store-list-item .store-name {
  display: block;
  width: 100%;
  padding-left: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: var(--font-size-body);
}

.store-name-cell::after {
  content: attr(data-full-name);
  font-size: var(--font-size-tooltip);
  line-height: 1.4;
  position: absolute;
  top: calc(100%);
  left: 0;
  background: var(--color-background-bright, #ffffff);
  color: var(--text-color, #414141);
  border: 0.0625rem solid var(--color-background, #e0e0e0);
  padding: 0.5rem 0.625rem;
  border-radius: 0.625rem;
  min-width: 12.5rem;
  max-width: 20rem;
  white-space: normal;
  word-break: normal;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.08);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.12s ease, visibility 0.12s ease;
  z-index: 1000;
  text-align: left;
}

.store-name-cell:hover::after {
  opacity: 1;
  visibility: visible;
}

.value-pair {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
}

.store-list-item .value {
  font-weight: 700;
  font-size: var(--font-size-body);
  color: var(--color-text);
}

.store-list-item .opportunity {
  font-weight: 700;
  font-size: var(--font-size-tooltip);
  color: var(--color-vidacorp-3);
}

.pagination {
  margin-top: 0.625rem;
  display: flex;
  justify-content: center;
  gap: 0.625rem;
  height: 5%;
}

.pagination button {
  padding: 0.3125rem 0.625rem;
  cursor: pointer;
  font-size: var(--font-size-caption);
  color: var(--color-text);
  opacity: 0.35;
}

.pagination button.active {
  color: var(--color-text);
  opacity: 1;
}

.pagination button:hover {
  color: var(--color-text);
  opacity: 1;
}
</style>
